import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { useCallback, useState, useEffect, useContext } from "react";

import {
  FeedItemResponse,
  UserResponse,
} from "@every.org/common/src/codecs/entities";
import { isValidUsername } from "@every.org/common/src/codecs/username";
import { TextSize } from "@every.org/common/src/display/text";
import { FollowRequestStatus } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  clientRouteMetas,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { constructCloudinaryUrl } from "@every.org/common/src/helpers/cloudinary";
import {
  MatchingTypesWithKey,
  ExcludeTypesWithKey,
  TypeOfPromise,
} from "@every.org/common/src/helpers/types";
import { getUserFullNameOrPlaceholder } from "@every.org/common/src/helpers/username";
import { UserProfileResponse } from "@every.org/common/src/routes/users";

import { IconDisplay, IconSize, Icon } from "src/components/Icon";
import { PageLoadingIndicator } from "src/components/LoadingIndicator";
import { UserHeaderCard } from "src/components/Profile/UserHeaderCard";
import { Redirect } from "src/components/Redirect";
import {
  DefaultTabNavigation,
  TabHeader,
  TabHeaderProps,
  NavigationTab,
} from "src/components/TabHeader";
import { AcceptFollowCard } from "src/components/User/AcceptFollowCard";
import { FundraisersTab } from "src/components/User/FundraisersTab";
import { FundsTab } from "src/components/User/FundsTab";
import { GiftsTab } from "src/components/User/GiftsTab";
import { JoinsTab } from "src/components/User/JoinsTab";
import { LikesTab } from "src/components/User/LikesTab";
import { UserPageTab } from "src/components/User/types";
import { NoMoreFeedItemsCallbackProp } from "src/components/feed";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import {
  headerCardPageSectionCss,
  PageSection,
} from "src/components/layout/PageSection";
import { AuthContext } from "src/context/AuthContext";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { AuthStatus } from "src/context/AuthContext/types";
import { UsersContext } from "src/context/UsersContext";
import { fetchUser } from "src/context/UsersContext/actions";
import { USER_NOT_FOUND, FETCHING_USER } from "src/context/UsersContext/types";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { NotFoundPage } from "src/pages/NotFound";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { verticalStackCss, spacing } from "src/theme/spacing";
import { ClientTrackingRoute, trackPageChange } from "src/utility/analytics";
import { logger } from "src/utility/logger";
import {
  OPENGRAPH_DIMENSIONS,
  overlayAvatarOnDefaultOpengraphCloudinaryParams,
} from "src/utility/opengraph";

const StyledDefaultTabNavigation = styled(DefaultTabNavigation)`
  ${cssForMediaSize({
    max: MediaSize.MEDIUM_SMALL,
    css: css`
      overflow-x: auto;
      overflow-y: hidden;
      li {
        margin-bottom: ${spacing.m};
      }
    `,
  })}
  ${NavigationTab} {
    ${cssForMediaSize({
      max: MediaSize.MEDIUM_SMALL,
      css: css`
        white-space: nowrap;
      `,
    })}
  }
`;

/**
 * Sentinel if user profile is being fetched, to distinguish missing value from
 * the profile not being found
 */
const FETCHING_USER_PROFILE = Symbol("FETCHING_USER_PROFILE");

/**
 * Customized tab header component for the User Page's tab navigation
 */
const UserTabHeader = ({
  count,
  ...rest
}: Omit<TabHeaderProps, "textSize" | "data-tname" | "children" | "ref"> & {
  count: number;
  selectedTab: UserPageTab;
  currentTab: UserPageTab;
}) => (
  <TabHeader
    {...rest}
    data-tname={`${rest.currentTab}_tab`}
    textSize={TextSize.m}
    count={count}
  >
    {rest.currentTab}
  </TabHeader>
);

/**
 * Component that renders the tab-based navigation at the top of the user page
 */
const UserTabNavigation = ({
  userId,
  username,
  selectedTab,
  fundCount,
  giftCount,
  likeCount,
  joinCount,
  fundraiserCount,
}: {
  selectedTab: UserPageTab;
  userId: UserResponse["id"];
  username: string;
  fundCount: number;
  giftCount: number;
  likeCount: number;
  joinCount: number;
  fundraiserCount: number;
}) => {
  const isOwnProfile = userId === useLoggedInUserOrUndefined()?.id;

  return (
    <StyledDefaultTabNavigation role="navigation" aria-label="User page tabs">
      {!!fundCount && (
        <li>
          <UserTabHeader
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.USER_LISTS,
              tokens: { username },
            })}
            selectedTab={selectedTab}
            currentTab={UserPageTab.LISTS}
            count={fundCount}
          />
        </li>
      )}
      {isOwnProfile || giftCount ? (
        <li>
          <UserTabHeader
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.USER_DONATIONS,
              tokens: { username },
            })}
            selectedTab={selectedTab}
            currentTab={UserPageTab.DONATIONS}
            count={giftCount}
          />
        </li>
      ) : null}
      {isOwnProfile || fundraiserCount ? (
        <li>
          <UserTabHeader
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.USER_FUNDRAISERS,
              tokens: { username },
            })}
            selectedTab={selectedTab}
            currentTab={UserPageTab.FUNDRAISERS}
            count={fundraiserCount}
          />
        </li>
      ) : null}
      {isOwnProfile || joinCount ? (
        <li>
          <UserTabHeader
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.USER_JOINS,
              tokens: { username },
            })}
            selectedTab={selectedTab}
            currentTab={UserPageTab.JOINS}
            count={joinCount}
          />
        </li>
      ) : null}
      {isOwnProfile || likeCount ? (
        <li>
          <UserTabHeader
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.USER_LIKES,
              tokens: { username },
            })}
            selectedTab={selectedTab}
            currentTab={UserPageTab.LIKES}
            count={likeCount}
          />
        </li>
      ) : null}
    </StyledDefaultTabNavigation>
  );
};

interface UserTabContentProps extends NoMoreFeedItemsCallbackProp {
  user: UserResponse;
  likeCount: number;
  giftCount: number;
  fundCount: number;
  joinCount: number;
  fundraiserCount: number;
  selectedTab: UserPageTab;
  donationsFeed?: FeedItemResponse[];
  joinsFeed?: FeedItemResponse[];
  likesFeed?: FeedItemResponse[];
  listFeed?: FeedItemResponse[];
}

/**
 * Renders the body content for a particular tab on the page
 */
const UserTabContent = (props: UserTabContentProps) => {
  const { getPathnameWithParams } = useEdoRouter();
  const { params, path } = getPathnameWithParams();
  const { username } = params;
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnProfile = username === loggedInUser?.username;
  const isBaseUserRoute = path === clientRouteMetas[ClientRouteName.USER].path;
  const failRedirectUrl = getRoutePath({
    format: URLFormat.RELATIVE,
    name: ClientRouteName.USER,
    tokens: { username },
  });

  switch (props.selectedTab) {
    case UserPageTab.LISTS:
      if (!isOwnProfile && props.fundCount === 0 && !isBaseUserRoute) {
        return <Redirect to={failRedirectUrl} />;
      }
      return (
        <FundsTab
          listFeed={props.listFeed}
          user={props.user}
          onNoMoreItems={props.onNoMoreItems}
        />
      );
    case UserPageTab.DONATIONS:
      if (!isOwnProfile && props.giftCount === 0 && !isBaseUserRoute) {
        return <Redirect to={failRedirectUrl} />;
      }
      return (
        <GiftsTab
          user={props.user}
          onNoMoreItems={props.onNoMoreItems}
          donationsFeed={props.donationsFeed}
        />
      );
    case UserPageTab.LIKES:
      return (
        <LikesTab
          likesFeed={props.likesFeed}
          user={props.user}
          onNoMoreItems={props.onNoMoreItems}
        />
      );
    case UserPageTab.JOINS:
      if (!isOwnProfile && props.joinCount === 0 && !isBaseUserRoute) {
        return <Redirect to={failRedirectUrl} />;
      }
      return (
        <JoinsTab
          joinsFeed={props.joinsFeed}
          user={props.user}
          onNoMoreItems={props.onNoMoreItems}
        />
      );
    case UserPageTab.FUNDRAISERS:
      if (!isOwnProfile && props.fundraiserCount === 0 && !isBaseUserRoute) {
        return <Redirect to={failRedirectUrl} />;
      }
      return (
        <FundraisersTab user={props.user} onNoMoreItems={props.onNoMoreItems} />
      );
    case UserPageTab.FOLLOWING:
      return <div />;

    case UserPageTab.FOLLOWERS:
      return <div />;
  }
};

/**
 * Hook to determine change the user that should be displayed
 */
const useIsSameUser = (
  userInfo: UserProfileResponse | typeof FETCHING_USER_PROFILE | null,
  username: string
) => {
  const nextUserName = username;
  const [prevUserName, setPrevUserName] = useState<string | null>(null);

  useEffect(() => {
    if (userInfo && userInfo !== FETCHING_USER_PROFILE) {
      if (userInfo.user.username) {
        setPrevUserName(userInfo.user.username);
      } else {
        logger.error({
          message: "Missing username in User instance",
          data: { userId: userInfo.user.id },
        });
      }
    }
  }, [userInfo]);

  const isSameUser =
    typeof prevUserName === "string" &&
    typeof nextUserName === "string" &&
    prevUserName === nextUserName;

  return isSameUser;
};

/**
 * TODO it would be nice to instead use the useUser hook and deprecate
 * this function, seems unnecessary.
 *
 * Hook wrapper to fetch the latest userInfo based on the matched parameters.
 *
 * @returns user profile data if received; FETCHING_USER_INFO if fetching; null
 * if user not found
 */
function useFetchUserInfo(initialUserInfo?: UserProfileResponse) {
  const { getPathnameWithParams } = useEdoRouter();
  const { params } = getPathnameWithParams();
  const { username } = params;
  const [userInfo, setUserInfo] = useState<
    UserProfileResponse | typeof FETCHING_USER_PROFILE | null
  >(initialUserInfo ? initialUserInfo : FETCHING_USER_PROFILE);

  const isSameUser = useIsSameUser(userInfo, username);

  const fetchInfo = useCallback(() => {
    // dont refetch the userif the user is already loaded
    // or user has not been changed
    if (username === null || isSameUser || !isValidUsername(username)) {
      return Promise.resolve(null);
    }
    return fetchUser({ username });
  }, [isSameUser, username]);

  type FetchResult = TypeOfPromise<ReturnType<typeof fetchInfo>>;
  const handleInfoResponse = useCallback((result: FetchResult) => {
    // don't clear the user if it's already been loaded, or else the header will
    // flash
    setUserInfo((curUserInfo) =>
      result === null && typeof curUserInfo === "object" ? curUserInfo : result
    );
  }, []);
  const handleFetchError = useCallback((e: Error) => {
    setUserInfo(null);
    logger.error({ message: "An error occurred fetching the user.", error: e });
  }, []);
  useAsyncEffect({
    asyncOperation: fetchInfo,
    handleResponse: handleInfoResponse,
    handleError: handleFetchError,
  });

  return userInfo;
}

/**
 * Hook to determine which tab should be selected on the user page, based on the
 * route and user data
 *
 * - if the route is specifically the gifts, likes, or causes routes, show those
 * - else, show the leftmost tab with at least one entry
 * @returns the selected tab, or null if still loading or userInfo not found
 */
function determineSelectedTab(
  pathname: string,
  userInfo: Omit<MatchingTypesWithKey<UserProfileResponse, "causes">, "user">,
  tab?: string
): UserPageTab {
  switch (pathname) {
    case clientRouteMetas[ClientRouteName.USER_FOLLOWERS].route: {
      return UserPageTab.FOLLOWERS;
    }
    case clientRouteMetas[ClientRouteName.USER_FOLLOWING].route: {
      return UserPageTab.FOLLOWING;
    }
    // eslint-disable-next-line no-fallthrough
    case "/[nonprofitSlugOrUsername]":
    case clientRouteMetas[ClientRouteName.USER].route:
      if (userInfo.giftCount) {
        return UserPageTab.DONATIONS;
      } else if (userInfo.fundraiserCount) {
        return UserPageTab.FUNDRAISERS;
      } else if (userInfo.joinCount) {
        return UserPageTab.JOINS;
      }
      return UserPageTab.LIKES;
    case clientRouteMetas[ClientRouteName.USER_LISTS].route:
      return UserPageTab.LISTS;
    case clientRouteMetas[ClientRouteName.USER_DONATIONS].route:
      return UserPageTab.DONATIONS;
    case clientRouteMetas[ClientRouteName.USER_LIKES].route:
      return UserPageTab.LIKES;
    case clientRouteMetas[ClientRouteName.USER_JOINS].route:
      return UserPageTab.JOINS;
    case clientRouteMetas[ClientRouteName.USER_FUNDRAISERS].route:
      return UserPageTab.FUNDRAISERS;
    default:
      throw new Error(
        "Unexpected: matched route for non-user profile page" + pathname
      );
  }
}

/**
 * Possible states the page body can be in
 */
enum UserDataStatus {
  LOADING = "LOADING",
  NOT_PERMITTED = "NOT_PERMITTED",
  LOADED = "LOADED",
}

/**
 * Props that express the possible states the page body can be in
 */
type UserBodyProps = (
  | { status: UserDataStatus.LOADING }
  | {
      status: UserDataStatus.NOT_PERMITTED;
      user: UserResponse;
      userInfo: Omit<
        ExcludeTypesWithKey<UserProfileResponse, "causes">,
        "user"
      >;
    }
  | {
      status: UserDataStatus.LOADED;
      user: UserResponse;
      userInfo: Omit<
        MatchingTypesWithKey<UserProfileResponse, "causes">,
        "user"
      >;
      selectedTab: UserPageTab;
    }
) &
  NoMoreFeedItemsCallbackProp & {
    donationsFeed?: FeedItemResponse[];
    joinsFeed?: FeedItemResponse[];
    likesFeed?: FeedItemResponse[];
    listFeed?: FeedItemResponse[];
  };

const USER_PAGE_TO_SELF = {
  [ClientRouteName.USER_DONATIONS]: ClientTrackingRoute.SELF_DONATIONS,
  [ClientRouteName.USER_LISTS]: ClientTrackingRoute.SELF_FUNDS,
  [ClientRouteName.USER_LIKES]: ClientTrackingRoute.SELF_LIKES,
  [ClientRouteName.USER_JOINS]: ClientTrackingRoute.SELF_JOINS,
  [ClientRouteName.USER_FUNDRAISERS]: ClientTrackingRoute.SELF_FUNDRAISERS,
};

const UserBody = (props: UserBodyProps) => {
  const { getPathnameWithParams } = useEdoRouter();
  const { params, path } = getPathnameWithParams();
  const { username } = params;
  const authState = useContext(AuthContext);
  const isOwnProfile = username === authState.user?.username;
  useEffect(() => {
    if (authState.status === AuthStatus.LOADING) {
      return;
    }
    trackPageChange(
      isOwnProfile ? USER_PAGE_TO_SELF[path] ?? ClientTrackingRoute.SELF : path,
      username
    );
  }, [isOwnProfile, username, authState.status, path]);

  switch (props.status) {
    case UserDataStatus.LOADING:
      return <PageLoadingIndicator />;
    case UserDataStatus.NOT_PERMITTED:
      return (
        <PageSection
          contentCss={[verticalStackCss.m, { alignItems: "center" }]}
        >
          <Icon
            iconImport={() => import("src/components/Icon/icons/LockIcon")}
            size={IconSize.MEDIUM}
            display={IconDisplay.SECONDARY}
          />
          <h3>This profile is private</h3>
          <p>Follow this person to see their profile.</p>
        </PageSection>
      );
    case UserDataStatus.LOADED:
      return (
        <section css={verticalStackCss.xl}>
          <PageSection>
            <UserTabNavigation
              userId={props.user.id}
              username={username}
              fundCount={props.userInfo.fundCount}
              giftCount={props.userInfo.giftCount}
              likeCount={props.userInfo.likeCount}
              joinCount={props.userInfo.joinCount}
              fundraiserCount={props.userInfo.fundraiserCount}
              selectedTab={props.selectedTab}
            />
          </PageSection>
          <UserTabContent
            selectedTab={props.selectedTab}
            user={props.user}
            giftCount={props.userInfo.giftCount}
            likeCount={props.userInfo.likeCount}
            fundCount={props.userInfo.fundCount}
            joinCount={props.userInfo.joinCount}
            fundraiserCount={props.userInfo.fundraiserCount}
            onNoMoreItems={props.onNoMoreItems}
            donationsFeed={props.donationsFeed}
            joinsFeed={props.joinsFeed}
            likesFeed={props.likesFeed}
            listFeed={props.listFeed}
          />
        </section>
      );
  }
};

/**
 * Potential errors that can prevent the page from loading normally
 */
enum UserPageError {
  /**
   * A user with the username provided by the route wasn't found.
   */
  NOT_FOUND = "NOT_FOUND",
}

/**
 * Hook to fetch and process necessary user data to display the page
 */
function useGatherUserData(
  username: string,
  initialUserInfo?: UserProfileResponse
): UserBodyProps | UserPageError {
  const userInfo = useFetchUserInfo(initialUserInfo);
  const userState = useContext(UsersContext);
  const { pathname, query, asPath, replace } = useRouter();

  // Calls to replace must happen within a useEffect hook as they will fail if
  // run server-side
  // https://nextjs.org/docs/messages/no-router-instance
  useEffect(() => {
    const usernameLowerCase = username.toLowerCase();
    if (username !== usernameLowerCase) {
      // Throw away the original username and keep it lowercase.
      // Couldn't make it work with something like /`/@${username}(/|$)`/
      replace(asPath.replace(/\/@([^/]+)(\/|$)/, `/@${usernameLowerCase}$2`));
    }
  }, [asPath, replace, username]);

  if (!isValidUsername(username)) {
    return UserPageError.NOT_FOUND;
  }
  // rather than using the user in the fetch response, get it from the context
  // in case data changes after the first load, like follow status
  const user = userState.usersByUsername.get(username);

  if (userInfo === FETCHING_USER_PROFILE || user === FETCHING_USER) {
    return { status: UserDataStatus.LOADING };
  }
  if (!userInfo || user === USER_NOT_FOUND) {
    return UserPageError.NOT_FOUND;
  }

  if (!user && !userInfo.user) {
    logger.error({
      message: "Unexpected: after fetch, User with username missing",
      // data: { username },
    });
    return UserPageError.NOT_FOUND;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userToReturn = user || (userInfo as any).user;

  if (
    userToReturn.isPrivate &&
    userToReturn.followedByCurrentUserStatus !== FollowRequestStatus.ACCEPTED
  ) {
    return {
      status: UserDataStatus.NOT_PERMITTED,
      user: user || userInfo.user,
      userInfo,
    };
  }

  const selectedTab = determineSelectedTab(
    pathname,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userInfo as any,
    Array.isArray(query.tab) ? query.tab[0] : query.tab
  );
  return {
    status: UserDataStatus.LOADED,
    user: userToReturn,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userInfo: userInfo as any,
    selectedTab,
  };
}

function useUserDataOrUndefined(userData: UserBodyProps | UserPageError) {
  if (
    userData === UserPageError.NOT_FOUND ||
    userData.status === UserDataStatus.LOADING ||
    userData.status === UserDataStatus.NOT_PERMITTED
  ) {
    return undefined;
  }
  return userData;
}
/**
 * If own profile and you edited your username, this redirects to /me to
 * redirect you to the page with your new username
 */
function useRedirectOnUsernameChange() {
  const { replace, getPathnameWithParams } = useEdoRouter();
  const { params } = getPathnameWithParams();
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnProfile =
    loggedInUser &&
    params?.username &&
    loggedInUser.username === params.username;

  /**
   * Stores the initial value for username in case of a username change, to
   * trigger a redirect
   */
  const [initialUsername, setInitialUsername] = useState(
    loggedInUser?.username
  );

  useEffect(() => {
    // set the initial username if it hasn't been set before and a username has
    // been found
    if (!initialUsername && loggedInUser?.username) {
      setInitialUsername(loggedInUser.username);
    }
  }, [initialUsername, loggedInUser]);

  if (
    isOwnProfile &&
    loggedInUser?.username &&
    initialUsername &&
    loggedInUser.username !== initialUsername
  ) {
    // If we detect that the username has changed from the saved one, redirect
    // to the new url.
    replace(
      getRoutePath({ format: URLFormat.RELATIVE, name: ClientRouteName.ME })
    );
  }
}

type UserPageProps = {
  username: string;
  userInfo?: UserProfileResponse;
  donationsFeed?: FeedItemResponse[];
  joinsFeed?: FeedItemResponse[];
  likesFeed?: FeedItemResponse[];
  listFeed?: FeedItemResponse[];
};

/**
 * Component that renders the user page, handling each of its subtabs as well.
 */
const UserPage: NextPage<UserPageProps> = ({
  username,
  userInfo,
  donationsFeed,
  joinsFeed,
  likesFeed,
  listFeed,
}) => {
  useRedirectOnUsernameChange();
  const userData = useGatherUserData(username, userInfo);
  const userDataOrUndefined = useUserDataOrUndefined(userData);
  const [hideFooter, setHideFooter] = useState<boolean>(
    userDataOrUndefined?.selectedTab !== UserPageTab.FUNDRAISERS
  );

  useEffect(() => {
    // never hide footer for fundraisers tab
    // always hide for others tabs on selected tab change
    setHideFooter(userDataOrUndefined?.selectedTab !== UserPageTab.FUNDRAISERS);
  }, [userDataOrUndefined?.selectedTab]);

  if (userData === UserPageError.NOT_FOUND) {
    return <NotFoundPage />;
  }

  const { pageTitle, metas } = getPageMeta(userData, username);

  return (
    <DefaultPageLayout
      key={username} // remount component if username has changed
      pageTitle={pageTitle}
      canonical={getRoutePath({
        name: ClientRouteName.USER,
        tokens: { username },
        format: URLFormat.ABSOLUTE,
      })}
      metas={metas}
      pageContentCss={[{ paddingTop: spacing.l }, verticalStackCss.xl]}
      hideFooter={hideFooter}
      philanthropyforEveryoneSuffix
    >
      {userData.status !== UserDataStatus.LOADING && (
        <PageSection
          contentCss={[verticalStackCss.l, headerCardPageSectionCss]}
        >
          <UserHeaderCard
            user={userData.user}
            followInfo={userData.userInfo.followInfo}
            totalDonated={userData.userInfo?.totalDonated}
            selectedTab={
              userData.status !== UserDataStatus.NOT_PERMITTED
                ? userData.selectedTab
                : UserPageTab.DONATIONS
            }
          />
          <AcceptFollowCard user={userData.user} />
        </PageSection>
      )}

      <UserBody
        {...userData}
        donationsFeed={donationsFeed}
        joinsFeed={joinsFeed}
        likesFeed={likesFeed}
        listFeed={listFeed}
        onNoMoreItems={() => {
          setHideFooter(false);
        }}
      />
    </DefaultPageLayout>
  );
};

const TAB_TO_PAGE_TITLE_MAP: { [key in UserPageTab]: string } = {
  [UserPageTab.LISTS]: "Lists",
  [UserPageTab.DONATIONS]: "Donations",
  [UserPageTab.LIKES]: "Likes",
  [UserPageTab.JOINS]: "Joins",
  [UserPageTab.FUNDRAISERS]: "Fundraisers",
  [UserPageTab.FOLLOWING]: "Following",
  [UserPageTab.FOLLOWERS]: "Followers",
};

/**
 * Get metadata to include on page
 */
function getPageMeta(userData: UserBodyProps, usernameInRoute: string) {
  const userName =
    "user" in userData
      ? getUserFullNameOrPlaceholder(userData.user)
      : undefined;
  const pageTitle = userName
    ? `${userName}${
        "selectedTab" in userData
          ? `'s ${TAB_TO_PAGE_TITLE_MAP[userData.selectedTab]}`
          : ""
      }`
    : `@${usernameInRoute}`;
  const opengraphImage =
    "userInfo" in userData
      ? userData.user.profileImageCloudinaryId &&
        constructCloudinaryUrl({
          ...overlayAvatarOnDefaultOpengraphCloudinaryParams({
            avatarCloudinaryId: userData.user.profileImageCloudinaryId,
          }),
          dimensions: OPENGRAPH_DIMENSIONS,
        })
      : null;

  const metas =
    "userInfo" in userData
      ? {
          "og:description": `Check out ${getUserFullNameOrPlaceholder(
            userData.user
          )}'s giving.`,
          ...(opengraphImage ? { "og:image": opengraphImage } : {}),
        }
      : {};
  return { pageTitle, metas };
}

export default UserPage;
